import { IfAbility } from '@efdigital/ll-addon-auth';
import { Heading } from '@efdigital/ll-addon-ui';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import {
  processSearchUsersRequest,
  SearchUsers,
} from '@efdigital/ll-admin-v2/components/search/search-users.gts';
import type LibraryUsersIndexController from '@efdigital/ll-admin-v2/controllers/library/users/index';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

interface LibraryUsersIndexSignature {
  Args: {
    controller: LibraryUsersIndexController;
  };
}

export default class LibraryUsersIndex extends Component<LibraryUsersIndexSignature> {
  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return '—';
    }

    return new Date(dateString).toLocaleString();
  }

  <template>
    <div class="container py-5">
      <IfAbility @ability="clients:users" @atLeast="viewer">
        <:if>
          <Heading @text="User management" />
          <p class="text-body-secondary mb-4">
            Search for a user by email, name, or ID. Select a result to view
            profile details and available actions.
          </p>

          <SearchUsers
            @legend="Search users"
            @state={{@controller}}
            @setState={{@controller.setState}}
            class="mb-4"
          >
            <:search as |search|>
              <Await @promise={{processSearchUsersRequest search}}>
                <:success as |results|>
                  <ResultsTable @data={{results}} data-test-user-search-results>
                    <:header>
                      <th scope="col">Email</th>
                      <th scope="col">User ID</th>
                      <th scope="col">Last login</th>
                    </:header>
                    <:row as |user|>
                      <tr>
                        <td>
                          <LinkTo
                            @route="library.users.user"
                            @model={{user.id}}
                            class="link-primary text-decoration-none"
                            data-test-user-search-result
                          >
                            {{user.title}}
                          </LinkTo>
                        </td>
                        <td class="font-monospace small">{{user.id}}</td>
                        <td>{{this.formatDate user.lastLogin}}</td>
                      </tr>
                    </:row>
                    <:empty>
                      <p
                        class="text-body-secondary"
                        data-test-user-search-empty
                      >
                        No users matched your search.
                      </p>
                    </:empty>
                  </ResultsTable>
                </:success>
                <:pending>
                  <p class="text-body-secondary" data-test-user-search-loading>
                    Searching…
                  </p>
                </:pending>
                <:error as |error|>
                  <div
                    class="alert alert-danger"
                    role="alert"
                    data-test-user-search-error
                  >
                    {{error}}
                  </div>
                </:error>
              </Await>
            </:search>
          </SearchUsers>
        </:if>
        <:else>
          <div class="alert alert-warning">
            You do not have permission to search or manage users.
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
